import React from 'react';
import { Bar, XAxis, YAxis, Tooltip, Legend, Line, ComposedChart, ResponsiveContainer } from 'recharts';
import { Box, Typography, Select, MenuItem, FormControl, Card } from '@mui/material';

const barColors = ['#F9AA33']; // Dashboard yellow
const lineColor = '#4caf50';   // Dashboard green

const HarvestsChart = ({ data, selectedPlantType, setSelectedPlantType, plantTypes }) => {
    // Find the max values for better scaling
    const maxHarvestTotal = Math.max(...data.map(d => d.harvestTotal || 0));
    const maxUnitPrice = Math.max(...data.map(d => d.averageUnitPrice || 0));

    return (
        <Card
            sx={{
                backgroundColor: '#181F2A',
                color: '#fff',
                borderRadius: 1.5,
                p: { xs: 2, md: 3 },
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
                boxShadow: '0 2px 8px 0 rgba(0,0,0,0.20)',
                mb: { xs: 2, md: 2 },
                minHeight: { xs: 350, md: 400 },
                maxHeight: { md: 586 }
            }}
        >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <Typography variant="h6" sx={{ fontSize: 14, fontWeight: 500, color: '#fff', flex: 1 }}>
                    Harvest Totals and Average Unit Prices by Month
                </Typography>
                <FormControl size="small" sx={{ minWidth: 120, height: 32 }}>
                    <Select
                        value={selectedPlantType}
                        onChange={(e) => setSelectedPlantType(e.target.value)}
                        sx={{
                            height: 32,
                            fontSize: 12,
                            color: '#fff',
                            bgcolor: 'rgba(255,255,255,0.05)',
                            '& .MuiSelect-select': {
                                py: 0.5,
                                pl: 1,
                            },
                            '& .MuiOutlinedInput-notchedOutline': {
                                borderColor: 'rgba(255,255,255,0.1)',
                            },
                            '&:hover .MuiOutlinedInput-notchedOutline': {
                                borderColor: 'rgba(255,255,255,0.2)',
                            },
                            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                borderColor: '#F9AA33',
                            },
                        }}
                    >
                        {plantTypes.map((plantType) => (
                            <MenuItem key={plantType} value={plantType} sx={{ fontSize: 12 }}>
                                {plantType}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </Box>
            <Typography sx={{ color: '#AAB4C8', fontWeight: 400, fontSize: 12, mb: 1, mt: -1 }}>
                {selectedPlantType}
            </Typography>
            <Box sx={{ flex: 1, minHeight: 280, height: { xs: 320, md: 520 } }}>
                <ResponsiveContainer width="100%" height={520}>
                    {data && data.length > 0 ? (
                        <ComposedChart data={data} margin={{ top: 20, right: 20, left: 20, bottom: 20 }}>
                            <XAxis
                                dataKey="monthYear"
                                stroke="#AAB4C8"
                                tick={{ fill: '#AAB4C8' }}
                                fontSize={12}
                            />
                            <YAxis
                                yAxisId="left"
                                stroke="#AAB4C8"
                                tick={{ fill: '#AAB4C8' }}
                                fontSize={12}
                                domain={[0, maxHarvestTotal + maxHarvestTotal * 0.1]}
                                label={{
                                    value: "Harvest Total (kg)",
                                    angle: -90,
                                    position: 'insideLeft',
                                    fill: '#AAB4C8',
                                    offset: 0,
                                    style: { textAnchor: 'middle' },
                                }}
                            />
                            <YAxis
                                yAxisId="right"
                                orientation="right"
                                stroke="#4caf50"
                                tick={{ fill: '#4caf50' }}
                                fontSize={12}
                                domain={[0, maxUnitPrice + maxUnitPrice * 0.1]}
                                label={{
                                    value: "Average Unit Price (NGN)",
                                    angle: -90,
                                    position: 'insideRight',
                                    fill: '#4caf50',
                                    offset: 0,
                                    style: { textAnchor: 'middle' },
                                }}
                            />
                            <Tooltip
                                formatter={(value, name) =>
                                    name === "Average Unit Price"
                                        ? `${value} NGN`
                                        : `${value} kg`
                                }
                                contentStyle={{
                                    backgroundColor: '#283144',
                                    border: '1px solid rgba(255,255,255,0.1)',
                                    borderRadius: 4,
                                    color: '#fff',
                                }}
                            />
                            <Legend wrapperStyle={{ color: '#AAB4C8' }} />
                            <Bar
                                yAxisId="left"
                                dataKey="harvestTotal"
                                fill={barColors[0]}
                                name="Harvest Total"
                            />
                            <Line
                                yAxisId="right"
                                dataKey="averageUnitPrice"
                                stroke={lineColor}
                                name="Average Unit Price"
                                dot={{ r: 4 }}
                                activeDot={{ r: 6 }}
                                type="monotone"
                                strokeWidth={2}
                            />
                        </ComposedChart>
                    ) : (
                        <Box display="flex" alignItems="center" justifyContent="center" height="100%">
                            <Typography sx={{ color: '#AAB4C8', textAlign: 'center', fontSize: 16 }}>No harvest data available for this crop type.</Typography>
                        </Box>
                    )}
                </ResponsiveContainer>
            </Box>
        </Card>
    );
};

export default HarvestsChart;
